import { EVENT_STATUSES, capabilitiesOf, type EventStatus } from './event-status.js';
import type { NextStepKey, NextStepState } from './next-steps.js';
import type { Permission } from './permissions.js';

/**
 * Gesperrte Schritte mit Grund (Plan 16, U5): Welche Bereiche und Schritte eines Basars sind je nach Status (noch)
 * nicht oder nicht mehr möglich? Reine Anzeigeregel: Der Server prüft weiter selbst. Die Schlüssel sind hier eigene
 * Namen, weil das Register `areas.ts` im Web liegt und von `shared` nicht importiert werden kann.
 *
 * - `annahme`: Warenannahme (nur im Status „Annahme“)
 * - `kasse`: Kasse (nur im Status „Verkauf“)
 * - `rueckgabe`: Rückgabe sortieren (nur im Status „Rückgabe“)
 * - `basar_schliessen`: Wechsel „Rückgabe“ nach „Geschlossen“ (erst nach dem Kassen-Abgleich)
 * - `abrechnen`: Verkaufsübersicht festschreiben (erst ab „Geschlossen“; die Vorschau bleibt davon unberührt)
 * - `auszahlung`: Auszahlungen an Verkäufer (erst nach dem Abrechnen)
 * - `teilen`: Basar teilen (erst ab „Veröffentlicht“)
 */
export const AREA_GATE_KEYS = [
  'annahme',
  'kasse',
  'rueckgabe',
  'basar_schliessen',
  'abrechnen',
  'auszahlung',
  'teilen',
] as const;
export type AreaGateKey = (typeof AREA_GATE_KEYS)[number];

export interface AreaGateStep {
  key: NextStepKey;
  state: NextStepState;
}

export type AreaGateResult = { enabled: true } | { enabled: false; reason: string };

const OPEN: AreaGateResult = { enabled: true };
const blocked = (reason: string): AreaGateResult => ({ enabled: false, reason });

function rank(status: EventStatus): number {
  return EVENT_STATUSES.indexOf(status);
}

/**
 * Entscheidet, ob ein Schritt freigegeben ist, und nennt sonst den Grund (kurzer Satz ohne Punkt-Zwang, nach Glossar).
 * `nextSteps` liefert der Server nur mit `event.manage`; ohne dieses Recht (oder ohne Liste) entscheidet allein der
 * Status.
 */
export function areaGate(
  area: AreaGateKey,
  status: EventStatus,
  nextSteps: readonly AreaGateStep[] | undefined,
  permissions: readonly Permission[],
): AreaGateResult {
  const steps = permissions.includes('event.manage') ? nextSteps : undefined;
  const registersBehind = steps?.some((s) => s.key === 'registers_behind' && s.state === 'open');
  const caps = capabilitiesOf(status);
  switch (area) {
    case 'annahme':
      if (caps.intakeOpen) return OPEN;
      return blocked(
        rank(status) < rank('intake')
          ? 'Erst ab dem Status „Annahme“ möglich'
          : 'Die Annahme ist beendet',
      );
    case 'kasse':
      if (caps.cashOpen) return OPEN;
      return blocked(
        rank(status) < rank('selling')
          ? 'Erst ab dem Status „Verkauf“ möglich'
          : 'Der Verkauf ist beendet',
      );
    case 'rueckgabe':
      if (caps.returnsOpen) return OPEN;
      return blocked(
        rank(status) < rank('returns')
          ? 'Erst ab dem Status „Rückgabe“ möglich'
          : 'Die Rückgabe ist beendet',
      );
    case 'basar_schliessen':
      if (status === 'returns') {
        return registersBehind === true ? blocked('Erst nach dem Abgleich aller Kassen') : OPEN;
      }
      return blocked(
        rank(status) < rank('returns')
          ? 'Erst nach der Rückgabe möglich'
          : 'Der Basar ist bereits geschlossen',
      );
    case 'abrechnen':
      if (status === 'closed') return OPEN;
      return blocked(
        rank(status) < rank('closed')
          ? 'Erst nach dem Schließen des Basars möglich'
          : 'Der Basar ist bereits abgerechnet',
      );
    case 'auszahlung':
      if (rank(status) >= rank('settled')) return OPEN;
      return blocked('Erst nach dem Abrechnen möglich');
    case 'teilen':
      if (rank(status) >= rank('published')) return OPEN;
      return blocked('Erst nach dem Veröffentlichen möglich');
  }
}
